import React, { useEffect } from 'react';
import { useMutation } from '@apollo/react-hooks';
import { CREATE_MORATORIUM_EVENT } from 'Admin/Partners/graphql';
import { Modal, FormInput, Row, Column } from 'Shared/components';
import { Button } from '@clearcover/atlas';
import { DateTime } from 'luxon';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useToast } from 'Shared/components';
import { useAuth0 } from 'Auth';
import { hasScopedPermission } from 'Shared/utils';
import styles from './styles.module.css';
import {
  validatePostalCodes,
  POSTAL_CODE_ERROR_MESSAGES,
} from './utils/validation';

interface CreateMoratoriumModalProps {
  showModal: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormValues {
  name: string;
  description: string;
  displayMessage: string;
  startDate: string;
  endDate: string;
  active: boolean;
  postalCodes: string;
  type: string;
}

const initialValues: FormValues = {
  name: '',
  description: '',
  displayMessage: '',
  startDate: '',
  endDate: '',
  active: false,
  postalCodes: '',
  type: '',
};

const EVENT_TYPE_OPTIONS = {
  storm: 'Storm',
  hurricane: 'Hurricane',
  wildfire: 'Wildfire',
  earthquake: 'Earthquake',
  flood: 'Flood',
};

// Custom Yup test for future date validation
const isFutureDate = (message: string) => {
  return Yup.string().test('is-future', message, function(value) {
    if (!value) return true; // Let the required validation handle empty values
    const selectedDateTime = DateTime.fromISO(value);
    const now = DateTime.now();

    // Compare full DateTime including time components
    return selectedDateTime > now;
  });
};

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  description: Yup.string().required('Required'),
  displayMessage: Yup.string().required('Required'),
  type: Yup.string().required('Required'),
  startDate: isFutureDate('Start date and time must be in the future').required(
    'Required'
  ),
  endDate: Yup.string()
    .required('Required')
    .test(
      'is-after-start',
      'End date and time must be after start date',
      function(value) {
        if (!value || !this.parent.startDate) return true;
        const endDate = DateTime.fromISO(value);
        const startDate = DateTime.fromISO(this.parent.startDate);
        return endDate > startDate;
      }
    ),
  postalCodes: Yup.string()
    .required('Required')
    .test(
      'valid-zip-codes',
      POSTAL_CODE_ERROR_MESSAGES.INVALID_FORMAT,
      function(value) {
        if (!value) return true; // Let required validation handle empty values
        const validCodes = validatePostalCodes(value);
        return validCodes !== null;
      }
    )
    .test('no-duplicates', POSTAL_CODE_ERROR_MESSAGES.DUPLICATES, function(
      value
    ) {
      if (!value) return true;
      const validCodes = validatePostalCodes(value);
      return validCodes !== null;
    }),
});

export const CreateMoratoriumModal: React.FC<CreateMoratoriumModalProps> = ({
  showModal,
  onClose,
  onSuccess,
}) => {
  const [createMoratoriumEvent] = useMutation(CREATE_MORATORIUM_EVENT);
  const minDateTime = DateTime.now().toFormat("yyyy-MM-dd'T'HH:mm");
  const { toast } = useToast();
  const { user } = useAuth0();

  const hasManagePermission = hasScopedPermission(
    user?.effectiveGrants || [],
    'partner',
    'moratoriums.update'
  );

  useEffect(() => {
    if (!hasManagePermission) {
      onClose();
      toast({
        message: 'You do not have permission to create moratorium events',
        type: 'error',
      });
    }
  }, [hasManagePermission, onClose, toast]);

  if (!hasManagePermission) {
    return null;
  }

  return (
    <Modal showModal={showModal} onClose={onClose}>
      <div className={styles.modalContent}>
        <h2>Create Moratorium Event</h2>
        <Formik<FormValues>
          initialValues={initialValues}
          validationSchema={validationSchema}
          validateOnBlur={false}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            try {
              const postalCodesArray = values.postalCodes
                .split(',')
                .map(code => code.trim())
                .filter(code => code.length > 0);

              await createMoratoriumEvent({
                variables: {
                  ...values,
                  postalCodes: postalCodesArray,
                },
              });
              resetForm();
              onSuccess();
              onClose();
            } catch (error) {
              console.error('Failed to create moratorium event:', error);
              toast({
                message: 'Failed to create moratorium event',
                type: 'error',
              });
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit,
            isSubmitting,
          }) => {
            const minEndDateTime = (values.startDate
              ? DateTime.fromISO(values.startDate)
              : DateTime.now()
            )
              .plus({ days: 1 })
              .toFormat("yyyy-MM-dd'T'HH:mm");

            return (
              <form onSubmit={handleSubmit} noValidate>
                <Row>
                  <Column variant="full">
                    <FormInput
                      variant="text"
                      label="Name"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      required
                      errorMessage={
                        touched.name && errors.name ? errors.name : undefined
                      }
                    />
                  </Column>
                </Row>

                <Row>
                  <Column variant="full">
                    <FormInput
                      variant="combobox"
                      label="Event Type"
                      name="type"
                      value={values.type}
                      options={EVENT_TYPE_OPTIONS}
                      onChange={(value: string) => {
                        handleChange({
                          target: {
                            name: 'type',
                            value,
                          },
                        });
                      }}
                      required
                      errorMessage={
                        touched.type && errors.type ? errors.type : undefined
                      }
                    />
                  </Column>
                </Row>

                <Row>
                  <Column variant="full">
                    <FormInput
                      variant="text"
                      label="Description"
                      name="description"
                      value={values.description}
                      onChange={handleChange}
                      required
                      errorMessage={
                        touched.description && errors.description
                          ? errors.description
                          : undefined
                      }
                    />
                  </Column>
                </Row>

                <Row>
                  <Column variant="full">
                    <FormInput
                      variant="text"
                      label="Display Message"
                      name="displayMessage"
                      value={values.displayMessage}
                      onChange={handleChange}
                      required
                      errorMessage={
                        touched.displayMessage && errors.displayMessage
                          ? errors.displayMessage
                          : undefined
                      }
                    />
                  </Column>
                </Row>

                <Row>
                  <Column variant="half">
                    <FormInput
                      variant="text"
                      type="datetime-local"
                      label="Start Date"
                      name="startDate"
                      value={values.startDate}
                      onChange={handleChange}
                      min={minDateTime}
                      required
                      errorMessage={
                        touched.startDate && errors.startDate
                          ? errors.startDate
                          : undefined
                      }
                    />
                  </Column>
                  <Column variant="half">
                    <FormInput
                      variant="text"
                      type="datetime-local"
                      label="End Date"
                      name="endDate"
                      value={values.endDate}
                      onChange={handleChange}
                      min={minEndDateTime}
                      required
                      errorMessage={
                        touched.endDate && errors.endDate
                          ? errors.endDate
                          : undefined
                      }
                    />
                  </Column>
                </Row>

                <Row>
                  <Column variant="full">
                    <FormInput
                      variant="radioButtonGroup"
                      label="Active"
                      name="active"
                      value={values.active ? 'active' : 'inactive'}
                      onChange={(value: string) => {
                        handleChange({
                          target: {
                            name: 'active',
                            value: value === 'active',
                          },
                        });
                      }}
                      options={{
                        active: 'Active',
                        inactive: 'Inactive',
                      }}
                      required
                    />
                  </Column>
                </Row>

                <Row>
                  <Column variant="full">
                    <FormInput
                      variant="textarea"
                      label="Postal Codes"
                      name="postalCodes"
                      value={values.postalCodes}
                      onChange={handleChange}
                      placeholder="Enter 5-digit ZIP codes separated by commas (e.g., 12345, 67890)"
                      required
                      errorMessage={
                        touched.postalCodes && errors.postalCodes
                          ? errors.postalCodes
                          : undefined
                      }
                    />
                  </Column>
                </Row>

                <div className={styles.modalActions}>
                  <Button
                    onClick={onClose}
                    variant="secondary"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    isSubmitting={isSubmitting}
                  >
                    Create
                  </Button>
                </div>
              </form>
            );
          }}
        </Formik>
      </div>
    </Modal>
  );
};
